import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { clientApi } from '@/services/api'
import { useClientStore } from '@/stores/clientStore'
import type { Client } from '@/types'
import { Plus, Archive, RotateCcw, Search } from 'lucide-react'
import toast from 'react-hot-toast'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button, buttonVariants } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'

export default function ClientList() {
  const { currentFirm, isLoading: authLoading } = useAuth()
  const [clients, setClients] = useState<Client[]>([])
  const [loading, setLoading] = useState(false)
  const [filter, setFilter] = useState<'all' | 'active' | 'archived'>('active')
  const [search, setSearch] = useState('')
  const setCurrentClient = useClientStore((s) => s.setCurrentClient)

  useEffect(() => {
    if (!currentFirm) return
    loadClients()
  }, [currentFirm?.id])

  async function loadClients() {
    setLoading(true)
    try {
      const res = await clientApi.list(currentFirm?.id)
      setClients(res.data.clients)
    } finally {
      setLoading(false)
    }
  }

  const filtered = clients
    .filter((c) => filter === 'all' || c.status === filter)
    .filter((c) => !search || c.name.toLowerCase().includes(search.toLowerCase()))

  async function handleArchive(client: Client) {
    if (client.status === 'active') {
      await clientApi.archive(client.id)
      toast.success('Client archived')
    } else {
      await clientApi.restore(client.id)
      toast.success('Client restored')
    }
    loadClients()
  }

  const counts = {
    active: clients.filter((c) => c.status === 'active').length,
    archived: clients.filter((c) => c.status === 'archived').length,
    all: clients.length,
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[17px] font-bold text-[#000A1E] leading-[24px]">Clients</h1>
          <p className="text-[13px] text-[rgba(0,10,30,0.75)] mt-0.5">Manage your client ledgers</p>
        </div>
        <Link to="/clients/new" className={cn(buttonVariants({ variant: 'create' }), 'gap-2')}>
          <Plus className="w-4 h-4" /> New Client
        </Link>
      </div>

      <Card>
        <CardHeader className="border-b px-5 py-3">
          <div className="flex items-center justify-between gap-4">
            <div className="flex gap-1 border-b-2 border-[#CCCED2]">
              {(['active', 'archived', 'all'] as const).map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={cn(
                    'px-4 py-3 text-[13px] -mb-[2px] border-b-[3px] capitalize transition-colors',
                    filter === f
                      ? 'text-[#000A1E] font-bold border-b-[#0078C8]'
                      : 'text-[#0078C8] font-normal border-b-transparent'
                  )}
                >
                  {f} ({counts[f]})
                </button>
              ))}
            </div>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[rgba(0,10,30,0.75)]" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search clients..."
                className="pl-8 h-8 w-52 text-[13px]"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading || authLoading ? (
            <div className="p-5 space-y-3">
              {[1, 2, 3].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="text-[13px]">Name</TableHead>
                  <TableHead className="text-[13px]">Type</TableHead>
                  <TableHead className="text-[13px]">Currency</TableHead>
                  <TableHead className="text-[13px]">Plan</TableHead>
                  <TableHead className="text-[13px]">Status</TableHead>
                  <TableHead className="text-right text-[13px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((client) => (
                  <TableRow key={client.id}>
                    <TableCell>
                      <Link
                        to={`/clients/${client.id}/dashboard`}
                        onClick={() => setCurrentClient(client)}
                        className="text-[15px] text-[#0078C8] font-bold hover:underline"
                      >
                        {client.name}
                      </Link>
                    </TableCell>
                    <TableCell className="text-[15px] text-[rgba(0,10,30,0.75)] capitalize">
                      {client.entity_type.replace('_', ' ')}
                    </TableCell>
                    <TableCell>
                      <span className="font-mono text-[13px] text-[rgba(0,10,30,0.75)]">{client.currency}</span>
                    </TableCell>
                    <TableCell>
                      <Badge variant={client.plan_tier === 'premium' ? 'info' : 'default'}>
                        {client.plan_tier}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={client.status === 'active' ? 'success' : 'default'}>
                        {client.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        onClick={() => handleArchive(client)}
                        title={client.status === 'active' ? 'Archive' : 'Restore'}
                      >
                        {client.status === 'active'
                          ? <Archive className="w-3.5 h-3.5" />
                          : <RotateCcw className="w-3.5 h-3.5" />}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {filtered.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="py-12 text-center text-[15px] text-[rgba(0,10,30,0.75)]">
                      {search ? 'No clients match your search.' : 'No clients found.'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
